import { ComponentType, Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal, PortalInjector } from '@angular/cdk/portal';
import { ComponentRef, Injectable, Injector } from '@angular/core';
import { CompletionStatus } from '../services/api/tutorial-api.service';
import { TUTORIAL_BUTTON_DATA, TutorialButtonRef } from './tutorial-button-ref';
import { TutorialButtonComponent } from './tutorial-button/tutorial-button.component';
import { TUTORIAL_PANEL_DATA, TutorialPanelRef } from './tutorial-panel-ref';

export interface TutorialData {
  buttonText?: string;
  completionStatus: CompletionStatus;
}

const DEFAULT_SETTINGS: TutorialData = {
  buttonText: 'PRIMEIROS PASSOS',
  completionStatus: {
    finalizado: false,
    coletas: false,
    contatos: false,
    integracoes: false,
  }
};

@Injectable()
export class TutorialService {
  constructor(
    private overlay: Overlay,
    private injector: Injector,
  ) { }

  public showButton<T>(panelComponentType: ComponentType<T>, data?: TutorialData): [ TutorialButtonRef, T ] {
    const positionStrategy = this.overlay.position()
      .global()
      .bottom('48px')
      .right('48px');

    const overlayRef = this.overlay.create({
      hasBackdrop: false,
      positionStrategy,
    });

    const settings = { ...DEFAULT_SETTINGS, ...data };

    const buttonRef = new TutorialButtonRef(overlayRef);

    const buttonComponent = this.attachButtonContainer(overlayRef, buttonRef, settings);

    const panelRef = new TutorialPanelRef(buttonComponent, buttonRef);

    const panelComponent = this.attachPanelContainer(panelComponentType, buttonComponent, panelRef, settings.completionStatus);

    return [ buttonRef, panelComponent ];
  }

  private attachButtonContainer(overlayRef: OverlayRef, buttonRef: TutorialButtonRef, data: TutorialData) {
    const injector = this.createButtonInjector(buttonRef, data.buttonText);

    const containerPortal = new ComponentPortal(TutorialButtonComponent, null, injector);
    const containerRef: ComponentRef<TutorialButtonComponent> = overlayRef.attach(containerPortal);

    return containerRef.instance;
  }

  private attachPanelContainer<T>(panelComponentType: ComponentType<T>,
                                  tutorialButtonComponent: TutorialButtonComponent,
                                  panelRef: TutorialPanelRef,
                                  data: CompletionStatus) {
    const injector = this.createPanelInjector(panelRef, data);

    tutorialButtonComponent.contentPortal = new ComponentPortal(panelComponentType, null, injector);
    if (!tutorialButtonComponent.contentOutlet) {
      return null;
    }
    const componentRef = tutorialButtonComponent.contentOutlet.attachedRef as ComponentRef<T>;

    return componentRef.instance;
  }

  private createButtonInjector(buttonRef: TutorialButtonRef, text: string): PortalInjector {
    const injectionTokens = new WeakMap();

    injectionTokens.set(TUTORIAL_BUTTON_DATA, text);
    injectionTokens.set(TutorialButtonRef, buttonRef);

    return new PortalInjector(this.injector, injectionTokens);
  }

  private createPanelInjector(panelRef: TutorialPanelRef, data: CompletionStatus): PortalInjector {
    const injectionTokens = new WeakMap();

    injectionTokens.set(TUTORIAL_PANEL_DATA, data);
    injectionTokens.set(TutorialPanelRef, panelRef);

    return new PortalInjector(this.injector, injectionTokens);
  }
}
